import * as React from 'react';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Money } from '../../../components/ui/Money';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu';
import type { ReceiptVoucher } from '../../../features/finance/hooks';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { Printer, Trash2 } from 'lucide-react';

interface ReceiptVoucherGridProps {
  vouchers: ReceiptVoucher[] | undefined;
  onPrint: (voucher: ReceiptVoucher) => void;
  onCancel: (voucher: ReceiptVoucher) => void;
  categoryNames: Record<string, string>;
}

export function ReceiptVoucherGrid({
  vouchers,
  onPrint,
  onCancel,
  categoryNames,
}: ReceiptVoucherGridProps) {
  return (
    <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
          <tr>
            <th className="p-4">Date & Time</th>
            <th className="p-4">Voucher No.</th>
            <th className="p-4">Depositor Name</th>
            <th className="p-4">Account / Type</th>
            <th className="p-4">Mode & Ref</th>
            <th className="p-4 text-right">Amount</th>
            <th className="p-4">Status</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-text-primary">
          {vouchers?.map((v) => (
            <tr key={v.id} className="hover:bg-surface-2 transition-colors">
              <td className="p-4 font-mono text-xs whitespace-nowrap">
                {new Date(v.voucherDate).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
              <td className="p-4 font-mono text-xs font-semibold">{v.voucherNumber}</td>
              <td className="p-4">
                <div className="flex flex-col">
                  <span className="font-semibold text-text-primary">{v.depositorName}</span>
                </div>
              </td>
              <td className="p-4">
                <span className="text-text-secondary">{v.category ? (categoryNames[v.category] || v.category) : '-'}</span>
              </td>
              <td className="p-4">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold">{v.paymentMode}</span>
                  {v.referenceNumber && (
                    <span className="font-mono text-xs text-text-muted mt-0.5">{v.referenceNumber}</span>
                  )}
                </div>
              </td>
              <td className="p-4 text-right font-semibold font-mono text-sm">
                <Money paise={v.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
              </td>
              <td className="p-4">
                <StatusBadge
                  status={v.status === 'APPROVED' ? 'success' : v.status === 'DRAFT' ? 'warning' : 'danger'}
                  label={v.status}
                />
              </td>
              <td className="p-4 text-right">
                <EntityActionMenu
                  actions={[
                    {
                      label: 'Print Voucher Receipt',
                      icon: <Printer className="w-4 h-4" />,
                      onClick: () => onPrint(v),
                    },
                    {
                      label: 'Cancel Voucher',
                      icon: <Trash2 className="w-4 h-4" />,
                      variant: 'destructive',
                      requiresConfirmation: true,
                      confirmTitle: 'Cancel Receipt Voucher',
                      confirmDescription: `Are you sure you want to cancel receipt voucher ${v.voucherNumber}? This will reverse the credit in the ledger.`,
                      confirmLabel: 'Cancel Voucher',
                      hidden: v.status !== 'APPROVED',
                      onClick: () => onCancel(v),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
